import React, { ReactElement } from 'react';
import AsyncPaginate from 'react-select-async-paginate';
import { AppFieldChildrenProps } from '../base';
import { ValueType } from 'react-select';
import { AppFieldSelectOption, ListValueType } from './select';
import { useTranslation } from 'react-i18next';
import { useAppAlertService } from '../../alerts/AppAlertService';
import { usePartnerService } from 'Services/partner';
import { useAppLogger } from 'Services/logger';

const defaultCountItems: number = 10;
const partnerExpand: any[] = [ 'tags' ];

const AppFieldAsyncSelectPartner = ( props: AppFieldChildrenProps<ListValueType> ): ReactElement | null => {
  const { ui, config, field, actions, controlId, readOnlyControlId } = props;
  const { t } = useTranslation( [ 'base' ] );
  const { showAlert, hideAlert } = useAppAlertService();
  const partnerService = usePartnerService();
  const logger = useAppLogger();
  const configuration = ui.configuration || {};
  const allowedTags = configuration.allowedTags || [];

  const [ selectedPartnerOption, setSelectedPartnerOption ] = React.useState<AppFieldSelectOption>( {
    label: '',
    value: '',
  } );
  const isFirstRender = React.useRef( true );

  const onChangeHandler = React.useCallback( ( value: ValueType<AppFieldSelectOption> ): void => {
    if ( value ) {
      if ( Array.isArray( value ) ) {
        const values = value.map( ( o: AppFieldSelectOption ) => o.value );
        actions.setFieldValue( field.name, values );
        setSelectedPartnerOption( value[ 0 ] );
      } else {
        const v = value as AppFieldSelectOption;
        actions.setFieldValue( field.name, v.value );
        setSelectedPartnerOption( v );
      }
    }
  }, [ actions, field.name ] );

  const loadOptions = async ( searchQuery, loadedOptions, { page } ) => {
    const emptyOptions = {
      options: [],
      hasMore: false,
      additional: {
        page,
      },
    };

    try {
      const query = ( searchQuery ?? '' ).trim();
      hideAlert();

      const request = {
        pageSize: defaultCountItems,
        pageToken: page,
        filter: allowedTags.length
          ? [ { id: 'tagSlugs', value: allowedTags.map( ( tag ) => tag.slug ).join() } ]
          : [],
        orderBy: [ { id: 'displayName', desc: false } ],
      };

      const response = await partnerService.listPartners( request, query );

      return {
        options: response.items ?
          response.items.map( ( i ) => {
            return {
              value: i.code,
              label: i.displayName,
            };
          } ) : [],
        hasMore: response.nextPageToken ? true : false,
        additional: {
          page: page + 1,
        },
      };
    } catch ( e ) {
      showAlert( {
        message: t( 'forms.messages.error' ),
        type: 'danger',
      } );

      return emptyOptions;
    }
  };

  React.useEffect( () => {
    if ( !isFirstRender.current ) {
      return;
    }

    let isMounted = true;

    const initPartnerSelection = async () => {
      if ( field.value && typeof field.value === 'string' && isFirstRender.current ) {
        isFirstRender.current = false;

        try {
          const defaultPartner = await partnerService.getPartner( field.value, partnerExpand );

          if ( isMounted ) {
            setSelectedPartnerOption ( {
              label: defaultPartner.displayName,
              value: defaultPartner.code,
            } );

          }
        } catch ( error ) {
          showAlert( {
            message: t( 'forms.messages.error' ),
            type: 'danger',
          } );
          logger.error( error );
        }
      }
    };

    initPartnerSelection();

    return () => {
      isMounted = false;
    };
  }, [ partnerService, showAlert, t, logger, field.value ] );


  if ( config.editingDisabled ) {
    // If selected option is not found (since partner values may not be loaded yet) use the field value as label
    return (
      <span id={ readOnlyControlId } className="app-field-plaintext">
        { selectedPartnerOption.label ? selectedPartnerOption.label : '' }
      </span>
    );
  }

  return (
    <AsyncPaginate
      debounceTimeout={ 0 }
      id={ controlId }
      className={ `app-field-select state-${ props.state }` }
      classNamePrefix={ 'af-select' }
      isDisabled={ config.editingDisabled || ( configuration['isDisabled'] && configuration['isDisabled'] ) }
      name={ field.name }
      placeholder= { ui.description }
      defaultValue={ selectedPartnerOption }
      value={ selectedPartnerOption }
      cacheOptions
      loadOptions={ loadOptions }
      blurInputOnSelect={ true }
      isMulti={ false }
      isClearable={ false }
      isSearchable={ true }
      escapeClearsValue={ true }
      menuPlacement="auto"
      onChange={ onChangeHandler }
      onBlur={ field.onBlur }
      additional={ {
        page: 1,
      } }
      noOptionsMessage={ () => t( 'users.noOptions' ) }
      loadingMessage={ () => t( 'roles.users.loading' ) }
      maxMenuHeight={ 300 }
    />
  );
};

export default AppFieldAsyncSelectPartner;

